import { useEffect, useState } from "react";
import qapi from "views_shared/app/javascript/api";

// Session-lived cache of the in-flight/settled read per (kind, owner), so every badge component that
// asks the same question during one session shares one request.
const badgeReads = new Map();

const cacheKey = (kind, ownerType, ownerId) => `${kind}|${ownerType}|${ownerId}`;

function loadBadge(kind, ownerType, ownerId) {
  const key = cacheKey(kind, ownerType, ownerId);
  if (badgeReads.has(key)) return badgeReads.get(key);

  const read = (async () => {
    try {
      const { data } = await qapi.badges.getForOwner(kind, ownerType, ownerId);
      // The endpoint exposes active badges only, so any row means the owner holds this badge.
      return Array.isArray(data) && data.length > 0;
    } catch {
      // Decorative read: any failure just means "no badge".
      return false;
    }
  })();

  badgeReads.set(key, read);
  return read;
}

/**
 * useBadge — does this owner currently hold this badge?
 *
 * Decorative read that rides beside the page reads (like the "Viewers also view" rail): it never
 * throws, never reports upward, and never blocks or fails the profile load. Returns false until the
 * read resolves, and stays false on any error.
 *
 * @param {string} kind - Badge kind, e.g. "qwoted100"
 * @param {string} ownerType - Owner model name, e.g. "Id::Profile::Promoter"
 * @param {number|string} [ownerId] - Owner record id (not a slug). Falsy = idle, no request.
 * @returns {boolean} true only once the read confirms the badge.
 */
function useBadge(kind, ownerType, ownerId) {
  const [held, setHeld] = useState(false);

  useEffect(() => {
    if (!ownerId) return undefined;

    let componentIsMounted = true;
    loadBadge(kind, ownerType, ownerId)
      .then((result) => {
        if (componentIsMounted) setHeld(result);
      })
      .catch(() => {
        if (componentIsMounted) setHeld(false);
      });

    return () => {
      componentIsMounted = false;
    };
  }, [kind, ownerType, ownerId]);

  return held;
}

// Test-only: drop the session cache so each test observes a fresh read (there is no runtime caller —
// the cache is meant to live for the whole session).
export function resetBadgeCache() {
  badgeReads.clear();
}

export default useBadge;
